package com.arena_web.ui.sections.profile.edit

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

/**
 * Mobile counterpart to [ProfileEditShell] — the same edit form rendered as a
 * [BottomSheet] instead of a centred desktop popup (Figma 8186:195575 /
 * 8171:173208 Personal Info, 8171:174103 / 8171:174363 Focus & Activities).
 *
 * The header drops the desktop description line: on mobile the sheet shows the
 * title + a `🌐 Public info` badge and a gradient hairline, matching the phone
 * designs. The body scrolls within the panel; the Cancel / Save row is pinned
 * at the bottom as two equal-width pills (Save tinted blue when enabled).
 *
 * The sheet renders under [BottomSheet], which sets `--scale: var(--mobile-scale)`
 * so the desktop `s()` / typography helpers map 1:1 onto the 390-px mobile
 * baseline the Figma designs are drawn at.
 */
@Composable
fun ProfileEditSheet(
    title: String,
    onClose: () -> Unit,
    onCancel: () -> Unit,
    onSave: () -> Unit,
    saveEnabled: Boolean = true,
    saveLabel: String? = null,
    showPublicBadge: Boolean = true,
    counterRow: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    val t = LocalAppStrings.current
    BottomSheet(onDismiss = onClose, contentGap = 16) {
        // Root flexes to fill the panel (up to 92vh) so the middle body can
        // scroll while header + buttons stay pinned. min-height:0 lets the
        // scroller actually shrink inside the flex column.
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(16)
                .styleModifier {
                    property("flex", "1 1 auto")
                    property("min-height", "0")
                }
        ) {
            // Header — title left, Public info badge right, gradient divider under.
            Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        title,
                        modifier = Modifier
                            .flexGrow(1)
                            .sFontSize(20)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(26)
                            .sLetterSpacing(0.2)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                    if (showPublicBadge) PublicInfoBadge()
                }
                SectionGradientDivider()
            }

            if (counterRow != null) {
                Box(modifier = Modifier.fillMaxWidth()) { counterRow() }
            }

            // Scrollable body — the only part that grows/scrolls.
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex", "1 1 auto")
                        property("min-height", "0")
                        property("overflow-y", "auto")
                        property("overflow-x", "hidden")
                        property("scrollbar-width", "none")
                        property("-ms-overflow-style", "none")
                    }
            ) {
                content()
            }

            // Action buttons — two equal-width pills (Figma 8196:385951).
            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SheetPillButton(
                    label = t.commonCancel,
                    primary = false,
                    enabled = true,
                    onClick = onCancel,
                    modifier = Modifier.flexGrow(1),
                )
                SheetPillButton(
                    label = saveLabel ?: t.commonSave,
                    primary = true,
                    enabled = saveEnabled,
                    onClick = { if (saveEnabled) onSave() },
                    modifier = Modifier.flexGrow(1),
                )
            }
        }
    }
}

@Composable
private fun PublicInfoBadge() {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.sGap(8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(src = AppIcons.Globe, modifier = Modifier.sSize(16))
        SpanText(
            t.profileEditSheetPublicInfo,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .sLetterSpacing(0.028)
                .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
        )
    }
}

/**
 * Full-width 44-px pill used in the sheet footer. `primary` paints the blue
 * Save fill; otherwise it's the translucent ghost surface with a gradient ring.
 * A disabled primary dims to 0.45 and ignores taps.
 */
@Composable
internal fun SheetPillButton(
    label: String,
    primary: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = modifier
            .sHeight(44)
            .sBorderRadius(100)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .styleModifier {
                property("position", "relative")
                if (primary) {
                    property("background-color", "#0061FF")
                    if (!enabled) property("opacity", "0.45")
                } else {
                    property("background", EDIT_PILL_BG)
                }
            }
            .onClick { if (enabled) onClick() },
        contentAlignment = Alignment.Center
    ) {
        GradientBorderRing(radiusPx = 100)
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(if (primary) org.jetbrains.compose.web.css.Color(sportColors.onPrimary.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}
